import { createContext, FC, PropsWithChildren, useState } from 'react'
import {
    LogsData,
    LogsDataSummary,
    LogsDataSummaryFilter,
    LogsDataWithTotalCount,
    RecentLogsData,
} from '../interfaces/LogsData'
import { SortParamOptions } from '../services/constant'

const defaultEndDate = new Date()
const defaultStartDate = new Date()
defaultStartDate.setDate(defaultEndDate.getDate() - 1)

interface LogsDataContextI {
    filter: LogsDataSummaryFilter
    setFilter: (filter: LogsDataSummaryFilter) => void

    loading: boolean
    setLoading: (loading: boolean) => void

    error: string | null
    setError: (error: string) => void

    fetchData: <R, P extends number>(
        apiCall: () => Promise<LogsDataWithTotalCount<R, P>>,
        setState: React.Dispatch<React.SetStateAction<R>>,
        setPagination: React.Dispatch<React.SetStateAction<P>>
    ) => void

    logsDataSummaryList: LogsDataSummary[]
    setLogsDataSummaryList: React.Dispatch<
        React.SetStateAction<LogsDataSummary[]>
    >
    logsData: LogsData[]
    setLogsData: React.Dispatch<React.SetStateAction<LogsData[]>>

    moduleName: string | undefined
    setModuleName: React.Dispatch<React.SetStateAction<string | undefined>>

    sortParamName: string | undefined
    setSortParamName: React.Dispatch<React.SetStateAction<string | undefined>>

    recentLogsData: RecentLogsData[]
    setRecentLogsData: React.Dispatch<React.SetStateAction<RecentLogsData[]>>

    recentLogTotalCount: number
    setRecentLogTotalCount: React.Dispatch<React.SetStateAction<number>>

    logsDataTotalCount: number
    setLogsDataTotalCount: React.Dispatch<React.SetStateAction<number>>

    logsDataSummaryTotalCount: number
    setLogsDataSummaryTotalCount: React.Dispatch<React.SetStateAction<number>>

    recentLogLimitPerPage: number
    setRecentLogLimitPerPage: React.Dispatch<React.SetStateAction<number>>

    logsDataLimitPerPage: number
    setLogsDataLimitPerPage: React.Dispatch<React.SetStateAction<number>>

    recentLogPages: number
    setRecentLogPages: React.Dispatch<React.SetStateAction<number>>

    logsDataPages: number
    setLogsDataPages: React.Dispatch<React.SetStateAction<number>>

    recentLogUrl: string
    setRecentLogUrl: React.Dispatch<React.SetStateAction<string>>
}
export const defaultFilter: LogsDataSummaryFilter = {
    contentText: '',
    endDate: defaultEndDate,
    startDate: defaultStartDate,
    responseTime: 1000,
    serverName: undefined,
    sortParam: SortParamOptions.OVERALL_COUNT,
    statusCode: '',
    tenant: '',
    urlText: '',
    business: '',
}
export const LogsDataContext = createContext<LogsDataContextI>({
    filter: defaultFilter,
    setFilter: () => {},
    loading: false,
    setLoading: () => {},
    error: null,
    setError: () => {},
    fetchData: () => {},
    logsDataSummaryList: [],
    setLogsDataSummaryList: () => {},
    logsData: [],
    setLogsData: () => {},
    moduleName: undefined,
    setModuleName: () => {},
    sortParamName: undefined,
    setSortParamName: () => {},
    recentLogsData: [],
    setRecentLogsData: () => {},
    recentLogTotalCount: 0,
    setRecentLogTotalCount: () => {},
    logsDataTotalCount: 0,
    setLogsDataTotalCount: () => {},
    logsDataSummaryTotalCount: 0,
    setLogsDataSummaryTotalCount: () => {},
    recentLogLimitPerPage: 10,
    setRecentLogLimitPerPage: () => {},
    logsDataLimitPerPage: 10,
    setLogsDataLimitPerPage: () => {},
    logsDataPages: 0,
    setLogsDataPages: () => {},
    recentLogPages: 0,
    setRecentLogPages: () => {},
    recentLogUrl: '',
    setRecentLogUrl: () => {},
})

export const LogsDataProvider: FC<PropsWithChildren> = ({ children }) => {
    const [filter, setFilter] = useState<LogsDataSummaryFilter>(defaultFilter)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>('')
    const [logsDataSummaryList, setLogsDataSummaryList] = useState<
        LogsDataSummary[]
    >([])
    const [logsData, setLogsData] = useState<LogsData[]>([])
    const [moduleName, setModuleName] = useState<string | undefined>(undefined)
    const [sortParamName, setSortParamName] = useState<string | undefined>(
        undefined
    )
    const [recentLogsData, setRecentLogsData] = useState<RecentLogsData[]>([])
    const [recentLogTotalCount, setRecentLogTotalCount] =
        useState<number>(0)
    const [logsDataTotalCount, setLogsDataTotalCount] =
        useState<number>(0)
    const [recentLogLimitPerPage, setRecentLogLimitPerPage] =
        useState<number>(10)
    const [logsDataLimitPerPage, setLogsDataLimitPerPage] = useState<number>(10)
    const [logsDataPages, setLogsDataPages] = useState<number>(0)
    const [recentLogPages, setRecentLogPages] = useState<number>(0)
    const [recentLogUrl, setRecentLogUrl] = useState<string>('')
    const [logsDataSummaryTotalCount, setLogsDataSummaryTotalCount] =
        useState<number>(0)

    const fetchData = async <T extends LogsDataWithTotalCount<any>>(
        apiCall: () => Promise<T>,
        setState: React.Dispatch<React.SetStateAction<T['result']>>,
        setPagination: React.Dispatch<React.SetStateAction<T['totalCount']>>
    ) => {
        setLoading(true)
        setError(null)
        try {
            const data = await apiCall()
            setPagination(data.totalCount)
            setState(data.result)
        } catch (err: any) {
            setError(err.message || 'Something went wrong')
        } finally {
            setLoading(false)
        }
    }

    return (
        <LogsDataContext.Provider
            value={{
                filter,
                setFilter,
                loading,
                setLoading,
                error,
                setError,
                fetchData,
                logsDataSummaryList,
                setLogsDataSummaryList,
                logsData,
                setLogsData,
                moduleName,
                setModuleName,
                recentLogsData,
                setRecentLogsData,
                sortParamName,
                setSortParamName,
                recentLogTotalCount,
                setRecentLogTotalCount,
                logsDataTotalCount,
                setLogsDataTotalCount,
                logsDataLimitPerPage,
                recentLogLimitPerPage,
                setLogsDataLimitPerPage,
                setRecentLogLimitPerPage,
                logsDataPages,
                setLogsDataPages,
                recentLogPages,
                setRecentLogPages,
                recentLogUrl,
                setRecentLogUrl,
                logsDataSummaryTotalCount,
                setLogsDataSummaryTotalCount,
            }}
        >
            {children}
        </LogsDataContext.Provider>
    )
}
